/*
 * (c) 2023 Amazon Web Services, Inc. or its affiliates. All Rights Reserved.
 *
 * This AWS Content is provided subject to the terms of the AWS Customer Agreement available at
 * http://aws.amazon.com/agreement or other written agreement between Customer and either
 * Amazon Web Services, Inc. or Amazon Web Services EMEA SARL or both.
 */

import React from "react";
import {
  Box,
  Button,
  Container,
  Flashbar,
  FormField,
  Header,
  Input,
  SpaceBetween,
  Spinner,
  Table,
} from "@cloudscape-design/components";
import { LambdaClient, InvokeCommand } from "@aws-sdk/client-lambda";
import { fetchAuthSession } from "aws-amplify/auth";
import { getStageConfig } from "../config";

const stageConfig = getStageConfig();
const assetConfig = stageConfig.assetSearchConfig;

interface Asset {
  assetId: string;
  identifier: string;
  itemName: string;
  productType?: string;
  marketplace?: string;
  qaSpinAssetId?: string;
  spin360AssetId?: string;
  thumbnailAssetId?: string;
  retargetingProfileName?: string;
  passedCerts?: string[];
  totalAssetSizeBytes?: number;
  totalTriangleCount?: number;
  colors?: string[];
  contributorType?: string;
  lastUpdatedTimestamp?: string;
}

interface DeduplicatedAsset extends Asset {
  modelKey: string;
}

interface FlashMessage {
  type: "error" | "warning" | "info" | "success";
  content: string;
  dismissible: boolean;
  id: string;
}

interface AssetSearchState {
  searchText: string;
  asinInput: string;
  loading: boolean;
  assets: Asset[];
  notifications: FlashMessage[];
  thumbnailUrls: Record<string, string>;
  selectedItems: (Asset | DeduplicatedAsset)[];
  downloading: boolean;
}

const PAGE_SIZE = 1000;

class AssetSearch extends React.Component<{}, AssetSearchState> {
  constructor(props: {}) {
    super(props);
    this.state = {
      searchText: "",
      asinInput: "",
      loading: false,
      assets: [],
      notifications: [],
      thumbnailUrls: {},
      selectedItems: [],
      downloading: false,
    };
  }

  private async getLambdaClient(): Promise<LambdaClient> {
    const session = await fetchAuthSession({ forceRefresh: false });
    if (!session.credentials) {
      throw new Error("No AWS credentials available. Please sign in again.");
    }
    return new LambdaClient({
      region: assetConfig.asinSearchLambdaRegion,
      credentials: {
        accessKeyId: session.credentials.accessKeyId,
        secretAccessKey: session.credentials.secretAccessKey,
        sessionToken: session.credentials.sessionToken,
      },
    });
  }

  private async invokeLambda(payload: object): Promise<any> {
    const client = await this.getLambdaClient();
    const command = new InvokeCommand({
      FunctionName: assetConfig.asinSearchLambdaName,
      Payload: new TextEncoder().encode(JSON.stringify(payload)),
    });
    const response = await client.send(command);
    if (response.FunctionError) {
      const errorBody = new TextDecoder().decode(response.Payload);
      throw new Error(`Lambda error: ${errorBody}`);
    }
    const body = new TextDecoder().decode(response.Payload);
    return JSON.parse(body);
  }

  private buildRequest(overrides: Partial<{
    freeformSearch: string;
    identifier: string;
    from: number;
    size: number;
  }> = {}) {
    return {
      operation: "getWoodshopAssets",
      arguments: {
        request: {
          assetType: "OUTPUT_GLB",
          size: PAGE_SIZE,
          from: 0,
          ...overrides,
        },
      },
    };
  }

  private getThumbnailSource(asset: Asset): { id: string; type: string } | null {
    if (asset.thumbnailAssetId) return { id: asset.thumbnailAssetId, type: "THUMBNAIL" };
    if (asset.qaSpinAssetId) return { id: asset.qaSpinAssetId, type: "QA_SPIN" };
    if (asset.spin360AssetId) return { id: asset.spin360AssetId, type: "OUTPUT_360SPIN" };
    return null;
  }

  private async getWoodshopLambdaClient(): Promise<LambdaClient> {
    const session = await fetchAuthSession({ forceRefresh: false });
    if (!session.credentials) {
      throw new Error("No AWS credentials available. Please sign in again.");
    }
    return new LambdaClient({
      region: assetConfig.getAssetLambdaRegion,
      credentials: {
        accessKeyId: session.credentials.accessKeyId,
        secretAccessKey: session.credentials.secretAccessKey,
        sessionToken: session.credentials.sessionToken,
      },
    });
  }

  private async fetchThumbnailUrl(assetId: string, assetType: string): Promise<string | null> {
    try {
      const client = await this.getWoodshopLambdaClient();
      const payload = {
        requestContext: {
          accountId: assetConfig.getAssetAccountId,
          resourcePath: "/assets/{assetType}/{assetKey}",
          httpMethod: "POST",
          identity: { user: "Workbench" },
        },
        path: `/assets/${assetType}/${assetId}`,
        httpMethod: "POST",
        body: JSON.stringify({
          assetKey: assetId,
          assetType: assetType,
          localFiles: [],
        }),
      };

      const command = new InvokeCommand({
        FunctionName: assetConfig.getAssetLambdaArn,
        Payload: new TextEncoder().encode(JSON.stringify(payload)),
      });
      const response = await client.send(command);

      if (response.FunctionError) {
        const errorBody = new TextDecoder().decode(response.Payload);
        console.warn(`GetAsset Lambda error for ${assetId}:`, errorBody);
        return null;
      }

      const result = JSON.parse(new TextDecoder().decode(response.Payload));

      let body = result;
      if (typeof result.body === "string") {
        body = JSON.parse(result.body);
      }
      const s3Files = body?.s3Files || [];
      const jpgFile = s3Files.find((f: any) => f.filename?.endsWith(".jpg"));
      const s3Url = jpgFile?.s3Url || s3Files[0]?.s3Url;
      return s3Url ? s3Url : null;
    } catch (err) {
      console.warn(`Failed to get thumbnail for ${assetId}:`, err);
      return null;
    }
  }

  private async loadThumbnails(assets: Asset[], _requestedAsins: string) {
    // Use the same dedup logic as the table — one thumbnail per model
    const deduped = this.deduplicateAssets(assets);

    const MAX_CONCURRENT = 5;
    const queue = deduped.filter((a) => this.getThumbnailSource(a) !== null);
    const urls: Record<string, string> = {};

    const processNext = async (): Promise<void> => {
      const asset = queue.shift();
      if (!asset) return;

      const source = this.getThumbnailSource(asset)!;
      const result = await this.fetchThumbnailUrl(source.id, source.type);
      if (result) {
        urls[asset.modelKey] = result;
        this.setState({ thumbnailUrls: { ...this.state.thumbnailUrls, ...urls } });
      }
      return processNext();
    };

    const workers = Array.from({ length: Math.min(MAX_CONCURRENT, queue.length) }, () => processNext());
    await Promise.all(workers);
  }

  private deduplicateAssets(assets: Asset[]): DeduplicatedAsset[] {
    // One row per unique model per ASIN.
    // First, collect all distinct qaSpinAssetIds per ASIN.
    const asinModels = new Map<string, Set<string>>();
    for (const asset of assets) {
      if (!asinModels.has(asset.identifier)) {
        asinModels.set(asset.identifier, new Set());
      }
      if (asset.qaSpinAssetId) {
        asinModels.get(asset.identifier)!.add(asset.qaSpinAssetId);
      }
    }

    // Now pick one representative asset per (ASIN + qaSpinAssetId) combo.
    // For ASINs with no qaSpinAssetId at all, pick one representative.
    const seen = new Set<string>();
    const result: DeduplicatedAsset[] = [];
    for (const asset of assets) {
      const models = asinModels.get(asset.identifier)!;
      let modelKey: string;

      if (models.size === 0) {
        // No qaSpinAssetId for this ASIN at all — one row
        modelKey = asset.identifier;
      } else if (!asset.qaSpinAssetId) {
        // This entry has null qaSpinAssetId but other entries for this ASIN do have one — skip
        continue;
      } else {
        modelKey = `${asset.identifier}:${asset.qaSpinAssetId}`;
      }

      if (!seen.has(modelKey)) {
        seen.add(modelKey);
        result.push({ ...asset, modelKey });
      }
    }
    return result;
  }


  handleSearch = async () => {
    const { searchText, asinInput } = this.state;
    if (!searchText.trim() && !asinInput.trim()) return;

    this.setState({ loading: true, notifications: [], assets: [], thumbnailUrls: {} });

    try {
      let identifiers = asinInput.trim();

      if (searchText.trim()) {
        const freeformResult = await this.invokeLambda(
          this.buildRequest({ freeformSearch: searchText.trim() })
        );
        console.log("[AssetSearch] Freeform response:", JSON.stringify(freeformResult, null, 2));
        const freeformIds = (freeformResult.assets || [])
          .map((a: Asset) => a.identifier)
          .filter(Boolean)
          .join(",");
        identifiers = [freeformIds, identifiers].filter(Boolean).join(",");
      }

      if (!identifiers) {
        this.setState({
          loading: false,
          notifications: [{ type: "warning", content: "No results found.", dismissible: true, id: "no-results" }],
        });
        return;
      }

      const result = await this.invokeLambda(
        this.buildRequest({ identifier: identifiers, from: 0, size: PAGE_SIZE })
      );
      console.log("[AssetSearch] Lambda response:", JSON.stringify(result, null, 2));

      const assets = result.assets || [];

      // Analyze: which ASINs from input are missing, which have multiple models
      const notifications: FlashMessage[] = [];
      const requested = identifiers.split(",").map((a: string) => a.trim()).filter(Boolean);

      // Group by ASIN, then count distinct models using qaSpinAssetId
      // Entries with null qaSpinAssetId are marketplace copies — not separate models
      const asinToModels = new Map<string, Set<string>>();
      for (const asset of assets) {
        if (!asinToModels.has(asset.identifier)) {
          asinToModels.set(asset.identifier, new Set());
        }
        if (asset.qaSpinAssetId) {
          asinToModels.get(asset.identifier)!.add(asset.qaSpinAssetId);
        }
      }

      // ASINs not found at all
      const missing = requested.filter((asin: string) => !asinToModels.has(asin));
      if (missing.length > 0) {
        notifications.push({
          type: "warning",
          content: `ASINs that did NOT return results (${missing.length}): ${missing.join(", ")}`,
          dismissible: true,
          id: "missing",
        });
      }

      // ASINs with multiple distinct models
      const multiples = Array.from(asinToModels.entries())
        .filter(([, models]) => models.size > 1)
        .map(([asin, models]) => `${asin} (${models.size} models)`);
      if (multiples.length > 0) {
        notifications.push({
          type: "info",
          content: `ASINs with multiple models: ${multiples.join(", ")}`,
          dismissible: true,
          id: "multiples",
        });
      }

      // Total model count = sum of unique models per ASIN
      const totalModels = Array.from(asinToModels.values())
        .reduce((sum, models) => sum + Math.max(models.size, 1), 0);

      notifications.unshift({
        type: "success",
        content: `Found ${asinToModels.size} of ${requested.length} ASINs (${totalModels} models)`,
        dismissible: true,
        id: "summary",
      });

      this.setState({ assets, loading: false, notifications });
      this.loadThumbnails(assets, identifiers);
    } catch (err: any) {
      console.error("Asset search failed:", err);
      this.setState({
        loading: false,
        notifications: [{ type: "error", content: err.message || "Search failed", dismissible: true, id: "error" }],
      });
    }
  };

  private extractFilename(url: string): string {
    try {
      const pathname = new URL(url).pathname;
      return pathname.split("/").pop() || "unknown";
    } catch {
      return "unknown";
    }
  }

  handleDownload = async () => {
    const { selectedItems, thumbnailUrls } = this.state;
    if (selectedItems.length === 0) return;

    this.setState({ downloading: true });

    try {
      const files: { asin: string; s3Url: string }[] = [];

      for (const item of selectedItems) {
        const key = (item as DeduplicatedAsset).modelKey || item.identifier;
        const url = thumbnailUrls[key];
        if (!url) continue;
        files.push({ asin: item.identifier, s3Url: url });
      }

      if (files.length === 0) {
        this.setState({
          downloading: false,
          notifications: [...this.state.notifications, {
            type: "warning", content: "No thumbnails available for selected items.", dismissible: true, id: "no-dl",
          }],
        });
        return;
      }

      const proxyUrl = assetConfig.imageProxyUrl;
      const response = await fetch(proxyUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ files }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: response.statusText }));
        throw new Error(err.error || "Download failed");
      }

      const result = await response.json();
      if (result.error) throw new Error(result.error);

      // Decode base64 data to blob
      const byteString = atob(result.data);
      const bytes = new Uint8Array(byteString.length);
      for (let i = 0; i < byteString.length; i++) {
        bytes[i] = byteString.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: result.contentType });

      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = result.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);

      this.setState({ downloading: false });
    } catch (err: any) {
      console.error("Download failed:", err);
      this.setState({
        downloading: false,
        notifications: [...this.state.notifications, {
          type: "error", content: `Download failed: ${err.message}`, dismissible: true, id: "dl-error",
        }],
      });
    }
  };

  render() {
    const { searchText, asinInput, loading, assets, notifications, thumbnailUrls, selectedItems, downloading } = this.state;
    const displayAssets = this.deduplicateAssets(assets);

    return (
      <SpaceBetween size="l">
        {notifications.length > 0 && (
          <Flashbar
            items={notifications.map((n) => ({
              ...n,
              onDismiss: () =>
                this.setState({ notifications: this.state.notifications.filter((x) => x.id !== n.id) }),
            }))}
          />
        )}

        <Container header={<Header variant="h2" description="Results reflect 3D models available in the Workbench3D application.">Asset Search</Header>}>
          <SpaceBetween size="m">
            <FormField label="Freeform Search" description="Search by product name or description">
              <Input
                value={searchText}
                onChange={({ detail }) => this.setState({ searchText: detail.value })}
                placeholder="e.g. wooden desk, office chair..."
                onKeyDown={({ detail }) => { if (detail.key === "Enter") this.handleSearch(); }}
              />
            </FormField>
            <FormField label="ASIN IDs" description="Comma-separated ASIN identifiers (optional)">
              <Input
                value={asinInput}
                onChange={({ detail }) => this.setState({ asinInput: detail.value })}
                placeholder="e.g. B08XYZ1234,B09ABC5678"
                onKeyDown={({ detail }) => { if (detail.key === "Enter") this.handleSearch(); }}
              />
            </FormField>
            <Button variant="primary" onClick={this.handleSearch} loading={loading}>
                Search
              </Button>
          </SpaceBetween>
        </Container>

        <Table
          columnDefinitions={[
            {
              id: "thumbnail",
              header: "Thumbnail",
              width: 100,
              cell: (item) => {
                const key = (item as DeduplicatedAsset).modelKey || item.identifier;
                const url = thumbnailUrls[key];
                const source = this.getThumbnailSource(item as Asset);
                if (!source) return "-";
                if (!url) return <Spinner size="normal" />;
                return (
                  <img
                    src={url}
                    alt={item.itemName || item.identifier || "Asset thumbnail"}
                    style={{ width: 80, height: 80, objectFit: "contain", borderRadius: 4, background: "#f0f0f0" }}
                  />
                );
              },
            },
            { id: "identifier", header: "ASIN", cell: (item) => item.identifier || "-", sortingField: "identifier" },
            { id: "itemName", header: "Item Name", cell: (item) => item.itemName || "-", sortingField: "itemName" },
            { id: "productType", header: "Product Type", cell: (item) => item.productType || "-", sortingField: "productType" },
            { id: "colors", header: "Colors", cell: (item) => item.colors?.join(", ") || "-" },
          ]}
          items={displayAssets}
          loading={loading}
          loadingText="Searching assets..."
          sortingDisabled={false}
          selectionType={assetConfig.imageProxyUrl ? "multi" : undefined}
          selectedItems={selectedItems}
          onSelectionChange={({ detail }) => this.setState({ selectedItems: detail.selectedItems })}
          trackBy={(item) => (item as DeduplicatedAsset).modelKey || item.identifier}
          empty={
            <Box textAlign="center" color="inherit">
              <b>No assets</b>
              <Box padding={{ bottom: "s" }} variant="p" color="inherit">
                Enter a search term or ASIN IDs above to find assets.
              </Box>
            </Box>
          }
          header={
            <Header
              counter={`(${displayAssets.length})`}
              actions={
                assetConfig.imageProxyUrl ? (
                  <Button
                    variant="primary"
                    onClick={this.handleDownload}
                    loading={downloading}
                    disabled={selectedItems.length === 0}
                  >
                    {selectedItems.length <= 1 ? "Download Image" : `Download ${selectedItems.length} Images`}
                  </Button>
                ) : undefined
              }
            >
              Results
            </Header>
          }
          stickyHeader
          variant="container"
        />
      </SpaceBetween>
    );
  }
}

export default AssetSearch;
